import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { AlertTriangle, Pill, NotebookText, MessageSquare, Send, BellRing, BellOff } from "lucide-react";
import { asArray, ALLERGY_SEVERITY_STYLES, getAllergySeverity } from "@/lib/clientFields";
import { enableWebPush, disableWebPush, isPushSupported, getPushSubscription } from "@/lib/pushNotifications";
import { BRAND, PoweredBy } from "@/components/Brand";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PortalView() {
  const { token } = useParams();
  const [feed, setFeed] = useState(null);
  const [thread, setThread] = useState([]);
  const [error, setError] = useState("");
  const [msg, setMsg] = useState({ sender_name: "", body: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [pushOn, setPushOn] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);
  const [pushMsg, setPushMsg] = useState("");
  const [pushSupported, setPushSupported] = useState(false);

  useEffect(() => {
    setPushSupported(isPushSupported());
    (async () => {
      const s = await getPushSubscription().catch(() => null);
      setPushOn(!!s);
    })();
  }, []);

  const togglePush = async () => {
    setPushBusy(true);
    setPushMsg("");
    try {
      if (pushOn) {
        await disableWebPush();
        setPushOn(false);
        setPushMsg("Push notifications disabled");
      } else {
        await enableWebPush({ portalToken: token });
        setPushOn(true);
        setPushMsg("You'll be notified when the care team replies");
      }
    } catch (e) {
      setPushMsg(e?.message || "Could not enable notifications");
    } finally { setPushBusy(false); }
  };

  const loadAll = () => {
    axios.get(`${API}/portal/feed/${token}`)
      .then((r) => setFeed(r.data))
      .catch((e) => setError(e?.response?.data?.detail || "Link invalid or expired"));
    axios.get(`${API}/portal/feed/${token}/thread`)
      .then((r) => setThread(r.data.messages || []))
      .catch(() => {});
  };

  useEffect(() => {
    loadAll();
  }, [token]);

  const sendMessage = async () => {
    if (!msg.sender_name.trim() || !msg.body.trim()) return;
    setSending(true);
    try {
      await axios.post(`${API}/portal/feed/${token}/messages`, msg);
      setSent(true);
      setMsg({ sender_name: msg.sender_name, body: "" });
      // refresh thread so the new message appears
      const r = await axios.get(`${API}/portal/feed/${token}/thread`);
      setThread(r.data.messages || []);
    } catch (e) {
      alert(e?.response?.data?.detail || "Could not send message");
    } finally { setSending(false); }
  };

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <div className="max-w-md bg-white border border-slate-200 p-8 text-center" data-testid="portal-error">
          <AlertTriangle className="w-8 h-8 text-red-600 mx-auto mb-3" />
          <div className="font-heading text-lg font-semibold text-slate-900 mb-1">This link is not available</div>
          <div className="text-sm text-slate-600">{error}</div>
        </div>
      </div>
    );
  }
  if (!feed) return <div className="min-h-screen flex items-center justify-center text-sm text-slate-500">Loading…</div>;

  const c = feed.client || {};
  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-slate-900 text-slate-100 px-6 py-4" data-testid="portal-header">
        <div className="max-w-3xl mx-auto flex items-center gap-3">
          <img src="/brand/icon-64.png" alt="Ayobell" className="w-9 h-9 object-contain shrink-0" />
          <div className="min-w-0">
            <div className="font-heading font-semibold tracking-tight">{BRAND.name} · Family Portal</div>
            <div className="text-[10px] text-emerald-300 uppercase tracking-widest">{BRAND.tagline}</div>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto p-6 space-y-6">
        <section className="bg-white border border-slate-200 p-5">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Service user</div>
          <div className="font-heading text-2xl font-semibold mt-1" data-testid="portal-client-name">{c.full_name || "—"}</div>
          {asArray(c.primary_needs).length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {asArray(c.primary_needs).map((n) => (
                <span key={n} className="px-2 py-0.5 text-xs rounded-full bg-sky-50 text-sky-700 border border-sky-200">{n}</span>
              ))}
            </div>
          )}
          {asArray(c.allergies).length > 0 && (
            <div className="mt-3 flex flex-wrap items-center gap-1.5">
              <span className="text-xs text-amber-800 inline-flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" /> Allergies:</span>
              {asArray(c.allergies).map((a) => (
                <span key={a} className={`px-2 py-0.5 text-xs rounded-full border ${ALLERGY_SEVERITY_STYLES[getAllergySeverity(a)]}`}>{a}</span>
              ))}
            </div>
          )}
          {feed.family_name && (
            <div className="text-xs text-slate-500 mt-3">Shared with {feed.family_name}{feed.relationship ? ` (${feed.relationship})` : ""}</div>
          )}
        </section>

        <section>
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-2 flex items-center gap-1.5">
            <NotebookText className="w-3.5 h-3.5" /> Recent daily logs
          </div>
          {feed.daily_logs.length === 0 ? (
            <div className="bg-white border border-slate-200 p-5 text-sm text-slate-500" data-testid="portal-daily-empty">Nothing recorded recently.</div>
          ) : (
            <ul className="space-y-2" data-testid="portal-daily-list">
              {feed.daily_logs.map((d, i) => (
                <li key={i} className="bg-white border border-slate-200 p-4">
                  <div className="flex items-baseline justify-between gap-3 flex-wrap">
                    <div className="font-medium text-slate-900">{d.title || d.activity}</div>
                    <div className="text-[11px] text-slate-500">{d.created_at ? new Date(d.created_at).toLocaleString() : ""}</div>
                  </div>
                  {d.notes && <div className="text-sm text-slate-700 mt-1 whitespace-pre-wrap">{d.notes}</div>}
                  {d.created_by_name && <div className="text-[11px] text-slate-500 mt-1">by {d.created_by_name}</div>}
                </li>
              ))}
            </ul>
          )}
        </section>

        <section>
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-2 flex items-center gap-1.5">
            <Pill className="w-3.5 h-3.5" /> Recent medication
          </div>
          {feed.emar.length === 0 ? (
            <div className="bg-white border border-slate-200 p-5 text-sm text-slate-500" data-testid="portal-emar-empty">No medication events shared.</div>
          ) : (
            <ul className="space-y-2" data-testid="portal-emar-list">
              {feed.emar.map((m, i) => (
                <li key={i} className="bg-white border border-slate-200 p-3 flex items-baseline justify-between gap-3 flex-wrap">
                  <div>
                    <div className="font-medium text-slate-900">{m.medication_name} {m.dose ? <span className="text-xs text-slate-500">· {m.dose}</span> : null}</div>
                    <div className="text-xs text-slate-600 capitalize">{m.action}</div>
                  </div>
                  <div className="text-[11px] text-slate-500">{m.given_at ? new Date(m.given_at).toLocaleString() : ""}</div>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section data-testid="portal-compose">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-2 flex items-center gap-1.5 justify-between">
            <span className="flex items-center gap-1.5"><MessageSquare className="w-3.5 h-3.5" /> Conversation with the care team</span>
            {pushSupported && (
              <button
                onClick={togglePush}
                disabled={pushBusy}
                data-testid="portal-toggle-push"
                className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest border border-slate-300 bg-white text-slate-700 px-2 py-1 hover:bg-slate-50 disabled:opacity-50 normal-case"
              >
                {pushOn ? <BellOff className="w-3 h-3" /> : <BellRing className="w-3 h-3" />}
                <span>{pushBusy ? "…" : pushOn ? "Notifications on" : "Notify me on reply"}</span>
              </button>
            )}
          </div>
          {pushMsg && <div className="mb-2 text-[11px] text-slate-600" data-testid="portal-push-msg">{pushMsg}</div>}
          <div className="bg-white border border-slate-200 p-4 space-y-3">
            {thread.length > 0 && (
              <ul className="space-y-2 mb-2" data-testid="portal-thread">
                {thread.map((m) => {
                  const isOut = m.direction === "out";
                  return (
                    <li key={m.id} className={`flex ${isOut ? "justify-start" : "justify-end"}`}>
                      <div className={`max-w-md p-3 border text-sm ${isOut ? "bg-slate-50 border-slate-200" : "bg-sky-50 border-sky-200"}`}>
                        <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">
                          {isOut ? `${m.sender_name} · care team` : `${m.sender_name} · family`}
                        </div>
                        <div className="whitespace-pre-wrap text-slate-900">{m.body}</div>
                        <div className="text-[10px] text-slate-400 mt-1">{m.created_at ? new Date(m.created_at).toLocaleString() : ""}</div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
            {sent && (
              <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 text-sm p-2" data-testid="portal-sent">
                Thanks — your note was shared with the care team.
              </div>
            )}
            <div>
              <label className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Your name</label>
              <input
                type="text"
                value={msg.sender_name}
                onChange={(e) => setMsg({ ...msg, sender_name: e.target.value })}
                className="w-full mt-1 h-10 px-3 border border-slate-300 bg-white text-sm"
                placeholder="e.g. Jane Smith"
                data-testid="portal-sender-name"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Message</label>
              <textarea
                rows={3}
                value={msg.body}
                onChange={(e) => setMsg({ ...msg, body: e.target.value })}
                className="w-full mt-1 p-3 border border-slate-300 bg-white text-sm"
                placeholder="Share an update, question or preference…"
                maxLength={2000}
                data-testid="portal-message-body"
              />
              <div className="text-[10px] text-slate-400 text-right mt-1">{msg.body.length}/2000</div>
            </div>
            <button
              onClick={sendMessage}
              disabled={sending || !msg.sender_name.trim() || !msg.body.trim()}
              data-testid="portal-send"
              className="inline-flex items-center gap-2 bg-slate-900 hover:bg-slate-800 disabled:opacity-50 text-white text-sm px-4 h-10"
            >
              <Send className="w-3.5 h-3.5" /> {sending ? "Sending…" : "Send to care team"}
            </button>
          </div>
        </section>

        <footer className="pt-4 pb-8 text-center text-[11px] text-slate-500 space-y-1">
          <div>Provided by Ayobell Care · Confidential</div>
          <div className="text-[10px] uppercase tracking-widest text-slate-400">Powered by TechSpace Solutions Ltd</div>
        </footer>
      </main>
    </div>
  );
}
